import { useMemo, useContext, useState, useEffect } from 'react';
import { images } from '../../../utils/api';
import { customFields } from '../../../utils/api/customFields';
import { TableContext } from '../../../components/shared/table/context/TableContext';
import { formatDate } from '../../../utils/dateUtils';
import { useAuth } from '../../../context/AuthContext';
import { ROW_HEIGHT } from '../../../components/shared/table/BaseTable';
import { getCertUrl } from '../../../utils/certUrls';
import TagBadge from '../../Documents/atoms/TagBadge';

// Helper function to determine text color based on background intensity
const getTextColor = (intensity) => {
  const intensityNum = parseInt(intensity);
  return intensityNum >= 500 ? 'text-white' : 'text-gray-900';
};

export const useColumns = (coins = [], showThumbnails) => {
  const { preferences: { density = 'standard', columnOrder = [] } } = useContext(TableContext);
  const { user } = useAuth();
  const [customFieldDefs, setCustomFieldDefs] = useState([]);
  const [customFieldValuesMap, setCustomFieldValuesMap] = useState({});

  // Load custom field definitions
  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const fields = await customFields.getAll();
        setCustomFieldDefs(fields);
      } catch (err) {
        console.error('Error loading custom fields:', err);
      }
    };

    loadCustomFields();
  }, []);

  // Load custom field values for a coin
  const loadCustomFieldValues = async (coinId) => {
    try {
      const values = await customFields.getValues(coinId);
      setCustomFieldValuesMap(prev => ({
        ...prev,
        [coinId]: values
      }));
    } catch (err) {
      console.error('Error loading custom field values:', err);
    }
  };

  const rowHeight = ROW_HEIGHT[density] || ROW_HEIGHT.standard;

  // Helper functions for safe value handling
  const renderCellSafely = (row, field, type, renderer) => {
    try {
      const value = row[field];
      // Show 0 for null/undefined population fields
      if ((field.includes('population') || field.includes('pop_') || field.includes('_pop')) && (value === null || value === undefined)) {
        return '0';
      }
      if (value === null || value === undefined) {
        return '-';
      }
      
      let typedValue;
      switch (type) {
        case 'number':
          typedValue = isNaN(Number(value)) ? null : Number(value);
          break;
        case 'date':
          typedValue = value instanceof Date ? value : new Date(value).toISOString();
          break;
        case 'boolean':
          typedValue = Boolean(value);
          break;
        default:
          typedValue = Array.isArray(value) ? value : String(value);
      }
      
      if (typedValue === null) {
        return '-';
      }
      
      return renderer(row, typedValue);
    } catch (error) {
      console.error('Error rendering cell:', error);
      return '-';
    }
  };

  // Use custom fields from the SQL response instead of fetching separately
  useEffect(() => {
    if (coins.length === 0) return;

    // Convert custom_fields from SQL response into our map format
    const valuesMap = {};
    coins.forEach(coin => {
      if (coin.custom_fields) {
        valuesMap[coin.id] = Object.entries(coin.custom_fields).reduce((acc, [name, value]) => {
          // Find the field definition to get its ID
          const field = customFieldDefs.find(f => f.name === name);
          if (field) {
            acc[field.id] = { value };
          }
          return acc;
        }, {});
      }
    });
    setCustomFieldValuesMap(valuesMap);
  }, [coins, customFieldDefs]);

  const columns = useMemo(() => {
    if (!coins.length) {
      return [];
    }

    const firstCoin = coins[0];
    
    // Start with image column if enabled
    const columns = showThumbnails ? [{
      field: 'image',
      headerName: 'Image',
      type: 'image',
      sortable: false,
      renderCell: (row) => {
        if (!row.imageUrl) return null;
        return (
          <div className="flex items-center justify-center overflow-hidden h-full">
            <div 
              className="relative"
              style={{
                height: `${ROW_HEIGHT[density]}px`,
                width: `${ROW_HEIGHT[density] * 1.5}px`
              }}
            >
              <img
                src={row.imageUrl}
                alt="Coin thumbnail"
                className="absolute inset-0 w-full h-full object-contain rounded"
                loading="lazy"
              />
            </div>
          </div>
        );
      }
    }] : [];

    // Add dynamic columns including custom fields
    columns.push(...Object.keys(firstCoin)
      .filter(key => !['images', 'imageUrl', 'id', 'status_id', 'status_color', 'created_at', 'updated_at', 'created_by', 'certification_service_id', 'tag_ids', 'transaction_coins', 'custom_fields', 'sale_date', 'sale_price'].includes(key))
      .map(key => {
        const value = firstCoin[key];
        const type = (() => {
          if (key === 'user_coin_id') return 'string';
          if (key === 'certification_service_id') return 'number';
          if (key === 'year') return 'string'; // Special handling for year
          if (key === 'mintage') return 'number';
          if (key === 'weight' || key === 'diameter') return 'number';
          if (key.includes('population') || key.includes('pop_') || key.includes('_pop')) return 'number';
          if (typeof value === 'number') return 'number';
          if (value instanceof Date) return 'date';
          if (typeof value === 'boolean') return 'boolean';
          if (key.includes('price') || key.includes('cost')) return 'number';
          if (key === 'purchase_date') return 'date';
          return 'string';
        })();

        // Custom header name mappings
        const headerNameMap = {
          'certification_service_name': 'TPG',
          'certification_service_id': 'TPG ID',
          'tags': 'Tags',
          'user_coin_id': 'Coin ID',
          'cac_sticker': 'CAC',
          'pcgs_number': 'PCGS #',
          'current_pop_higher': 'Pop Higher (Current)',
          'pop_higher_when_bought': 'Pop Higher (Bought)',
          'current_population': 'Pop (Current)',
          'population_when_bought': 'Pop (Bought)',
          'price_guide_value': 'Price Guide',
          'certification_number': 'Cert #',
          'minor_variety': 'Min. Variety',
          'status_name': 'Status',
          'storage_location': 'Storage Location',
          'reference_number': 'Ref. #',
          'assayer': 'Assayer'
        };

        const baseColumn = {
          field: key,
          headerName: headerNameMap[key] || (key === 'id' ? 'ID' : key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')),
          type,
          sortable: true,
          filterOperators: type === 'number' ? [
            { value: 'equals', label: 'Equals' },
            { value: 'notEquals', label: 'Not equals' },
            { value: '>', label: 'Greater than' },
            { value: '<', label: 'Less than' },
            { value: '>=', label: 'Greater than or equals' },
            { value: '<=', label: 'Less than or equals' },
            { value: 'isEmpty', label: 'Is empty' },
            { value: 'isNotEmpty', label: 'Is not empty' }
          ] : type === 'date' ? [
            { value: 'equals', label: 'Equals' },
            { value: 'notEquals', label: 'Not equals' },
            { value: '>', label: 'After' },
            { value: '<', label: 'Before' },
            { value: 'isEmpty', label: 'Is empty' },
            { value: 'isNotEmpty', label: 'Is not empty' }
          ] : type === 'boolean' ? [
            { value: 'equals', label: 'Equals' },
            { value: 'notEquals', label: 'Not equals' }
          ] : [
            { value: 'contains', label: 'Contains' },
            { value: 'equals', label: 'Equals' },
            { value: 'startsWith', label: 'Starts with' },
            { value: 'endsWith', label: 'Ends with' },
            { value: 'isEmpty', label: 'Is empty' },
            { value: 'isNotEmpty', label: 'Is not empty' },
            { value: 'containsAny', label: 'Contains Any (comma-separated)' }
          ]
        };

        // Add type-specific configurations
        switch (type) {
          case 'date':
            return {
              ...baseColumn,
              renderCell: (row) => renderCellSafely(row, key, 'string',
                (r, value) => {
                  try {
                    return formatDate(value, { user });
                  } catch (error) {
                    console.error('Error formatting date:', error);
                    return '-';
                  }
                }
              )
            };

          case 'number':
            if (['purchase_price', 'shipping_cost', 'grading_cost', 'total_cost', 'price_guide_value', 'list_price'].includes(key)) {
              return {
                ...baseColumn,
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;
                  // Convert to numbers and compare
                  return parseFloat(v1) - parseFloat(v2);
                },
                renderCell: (row) => renderCellSafely(row, key, 'number',
                  (r, value) => {
                    const hasDecimals = value % 1 !== 0;
                    return (
                      <span>
                        {`$${value.toLocaleString(undefined, {
                          minimumFractionDigits: hasDecimals ? 2 : 0,
                          maximumFractionDigits: hasDecimals ? 2 : 0
                        })}`}
                      </span>
                    );
                  }
                )
              };
            }
            // Special handling for mintage field to support text values while sorting numerically
            if (key === 'mintage') {
              return {
                ...baseColumn,
                type: 'string', // Change type to string since we store text now
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;

                  // Extract numeric string, removing commas and any text
                  const getNumericString = (str) => {
                    const noCommas = str.replace(/,/g, '');
                    const match = noCommas.match(/\d+/);
                    if (!match) return '0';
                    // Pad with zeros to ensure proper string comparison
                    return match[0].padStart(20, '0');
                  };

                  return getNumericString(v1).localeCompare(getNumericString(v2));
                },
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => {
                    if (!value) return value;
                    // Find the first number in the string
                    const match = value.match(/(\d+)/);
                    if (!match) return value;
                    
                    // Get the number and its position
                    const number = match[0];
                    const index = value.indexOf(number);
                    
                    // Format number with commas
                    const formattedNumber = parseInt(number.replace(/,/g, '')).toLocaleString('en-US');
                    
                    // Return the string with the formatted number
                    return value.slice(0, index) + formattedNumber + value.slice(index + number.length);
                  }
                )
              };
            }
            // Handle composition fields
            if (key === 'composition') {
              return {
                ...baseColumn,
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => {
                    // Convert to string if it's a number
                    const str = value.toString();
                    
                    // Remove trailing zeros and decimal point
                    const parts = str.split('.');
                    if (parts.length === 1) return parts[0]; // No decimal point
                    
                    // Remove trailing zeros from decimal part
                    const decimal = parts[1].replace(/0+$/, '');
                    return decimal ? `${parts[0]}.${decimal}` : parts[0];
                  }
                )
              };
            }
            // Handle weight and diameter fields
            if (key === 'weight' || key === 'diameter') {
              return {
                ...baseColumn,
                renderCell: (row) => renderCellSafely(row, key, 'number',
                  (r, value) => {
                    if (typeof value !== 'number') return value;
                    return `${value.toLocaleString('en-US')}${key === 'weight' ? 'g' : 'mm'}`;
                  }
                )
              };
            }
            // Population fields
            if (key.includes('population') || key.includes('pop_') || key.includes('_pop')) {
              return {
                ...baseColumn,
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;
                  // Convert to numbers and compare
                  return parseFloat(v1) - parseFloat(v2);
                },
                renderCell: (row) => renderCellSafely(row, key, 'number',
                  (r, value) => typeof value === 'number' ? value.toLocaleString('en-US') : value
                )
              };
            }
            // Default number rendering
            return {
              ...baseColumn,
              renderCell: (row) => renderCellSafely(row, key, 'number',
                (r, value) => typeof value === 'number' ? value.toLocaleString('en-US') : value
              )
            };
          case 'boolean':
            return {
              ...baseColumn,
              renderCell: (row) => renderCellSafely(row, key, 'boolean',
                (r, value) => value ? 'Yes' : 'No'
              )
            };

          default:
            // Special handling for denomination, TPG and year fields
            if (key === 'denomination') {
              return {
                ...baseColumn,
                type: 'string',
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;

                  // Normalize strings and remove commas
                  const normalize = str => str.toLowerCase().replace(/,/g, '');
                  const v1Norm = normalize(v1);
                  const v2Norm = normalize(v2);

                  // Helper to check if string starts with a number (including fractions and currency symbols)
                  const startsWithNumber = str => /^([$¥€£]?\d+(\s*\/\s*\d+)?|\d*\.?\d+)/.test(str);

                  // Helper to extract numeric value from the start of string
                  const getLeadingValue = str => {
                    // Remove currency symbols first
                    const withoutCurrency = str.replace(/^[$¥€£]/, '');
                    
                    // Try to match fraction at start
                    const fractionMatch = withoutCurrency.match(/^(\d+)\s*\/\s*(\d+)/);
                    if (fractionMatch) {
                      return parseFloat(fractionMatch[1]) / parseFloat(fractionMatch[2]);
                    }
                    // Try to match decimal or whole number at start
                    const numberMatch = withoutCurrency.match(/^(\d*\.?\d+)/);
                    return numberMatch ? parseFloat(numberMatch[1]) : null;
                  };

                  const v1StartsWithNum = startsWithNumber(v1Norm);
                  const v2StartsWithNum = startsWithNumber(v2Norm);

                  // If both start with numbers, compare numerically
                  if (v1StartsWithNum && v2StartsWithNum) {
                    const num1 = getLeadingValue(v1Norm);
                    const num2 = getLeadingValue(v2Norm);
                    if (num1 !== num2) {
                      return num1 - num2;
                    }
                    // If numbers are equal, fall through to alphabetical comparison
                  }
                  
                  // If only one starts with a number, prioritize it
                  if (v1StartsWithNum && !v2StartsWithNum) return -1;
                  if (!v1StartsWithNum && v2StartsWithNum) return 1;

                  // If neither starts with a number or numbers were equal, sort alphabetically
                  return v1Norm.localeCompare(v2Norm);
                }
              };
            } else if (key === 'certification_service_name') {
              return {
                ...baseColumn,
                type: 'string',
                // Use default alphabetical sorting
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;
                  return v1.localeCompare(v2);
                }
              };
            } else if (key === 'year') {
              return {
                ...baseColumn,
                type: 'string',
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;

                  // Helper to check if a string contains numbers
                  const hasNumbers = str => /\d/.test(str);
                  
                  // If both values have numbers, compare the numeric parts
                  if (hasNumbers(v1) && hasNumbers(v2)) {
                    const getYear = str => {
                      const match = str.match(/\d+/);
                      return match ? parseInt(match[0], 10) : 0;
                    };
                    return getYear(v1) - getYear(v2);
                  }
                  
                  // If only one has numbers, prioritize numeric years
                  if (hasNumbers(v1)) return -1;
                  if (hasNumbers(v2)) return 1;
                  
                  // If neither has numbers, sort alphabetically
                  return v1.localeCompare(v2);
                }
              };
            }
            // Handle special cases
            if (key === 'tags') {
              return {
                ...baseColumn,
                sortComparator: (v1, v2) => {
                  // Handle null/undefined/empty array values
                  if (!v1 && !v2) return 0;
                  if (!v1 || !v1.length) return -1;
                  if (!v2 || !v2.length) return 1;

                  // Get first tag names for comparison (case insensitive)
                  const name1 = v1[0]?.name?.toLowerCase() || '';
                  const name2 = v2[0]?.name?.toLowerCase() || '';
                  
                  return name1.localeCompare(name2);
                },
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => (
                    <div className="flex gap-2">
                      {(value || []).map(tag => (
                        <div
                          key={tag.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.location.href = `/sets?tagId=${tag.id}`;
                          }}
                          className="cursor-pointer"
                        >
                          <TagBadge
                            name={tag.name}
                            color={tag.color || '#60A5FA'} // Use blue-400 as fallback to match the lighter blue theme
                            interactive={false}
                          />
                        </div>
                      ))}
                    </div>
                  )
                )
              };
            } else if (key === 'status_name') {
              return {
                ...baseColumn,
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => (
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      r?.status_color ? `bg-${r.status_color} ${getTextColor(r.status_color.split('-')[1])}` : 'bg-blue-100 text-blue-800'
                    }`}>
                      {value}
                    </span>
                  )
                )
              };
            }
            
            // Handle special cases
            if (key === 'cac_sticker') {
              return {
                ...baseColumn,
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => {
                    if (!value || value === 'none') return null;
                    return (
                      <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold ${
                        value === 'gold' 
                          ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          : 'bg-green-100 text-green-800 border border-green-200'
                      }`}>
                        CAC
                      </span>
                    );
                  }
                )
              };
            }
            
            // Special handling for certification number
            if (key === 'certification_number') {
              return {
                ...baseColumn,
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => {
                    const certUrl = getCertUrl(value, r.certification_service_name, r.grade);
                    return certUrl ? (
                      <a
                        href={certUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={(e) => e.stopPropagation()} // Prevent row click
                      >
                        {value}
                      </a>
                    ) : value.toString();
                  }
                )
              };
            }
            
            // Special handling for coin_details to show HTML preview and tooltip with case-insensitive sorting
            if (key === 'coin_details') {
              return {
                ...baseColumn,
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;
                  // Strip HTML tags and do case-insensitive comparison
                  const stripHtml = str => str.replace(/<[^>]*>/g, '');
                  const text1 = stripHtml(v1);
                  const text2 = stripHtml(v2);
                  return text1.localeCompare(text2, undefined, { sensitivity: 'base' });
                },
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => (
                    <div className="relative group">
                      {/* Preview - fixed height with overflow hidden */}
                      <div 
                        className="truncate max-w-xs overflow-hidden"
                        style={{ 
                          height: '20px',
                          lineHeight: '20px'
                        }}
                        dangerouslySetInnerHTML={{ __html: value }} 
                      />
                      
                      {/* Tooltip */}
                      <div className="fixed z-[9999] invisible group-hover:visible bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-w-lg max-h-96 overflow-auto transform translate-y-0">
                        <div dangerouslySetInnerHTML={{ __html: value }} />
                      </div>
                    </div>
                  )
                )
              };
            }
            
            // Grade field
            if (key === 'grade') {
              return {
                ...baseColumn,
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;

                  // Helper functions
                  const getGradeScore = (str) => {
                    // Normalize the grade string
                    const grade = str.toUpperCase();
                    
                    // Extract any number from the grade string
                    const numMatch = grade.match(/\d+/);
                    if (numMatch) {
                      const num = parseInt(numMatch[0], 10);
                      // Only use numbers between 1-70 for numeric sorting
                      if (num >= 1 && num <= 70) {
                        return num;
                      }
                    }
                    return -1; // No valid numeric grade (1-70), will be sorted alphabetically
                  };

                  const score1 = getGradeScore(v1);
                  const score2 = getGradeScore(v2);

                  // If both are non-numeric or equal scores, sort alphabetically
                  if (score1 === -1 && score2 === -1 || score1 === score2) {
                    return v1.localeCompare(v2, undefined, { sensitivity: 'base' });
                  }

                  // Sort in ascending order (lower grades first)
                  return score1 - score2;
                },
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => value.toString()
                )
              };
            }
            
            // PCGS number field
            if (key === 'pcgs_number') {
              return {
                ...baseColumn,
                sortComparator: (v1, v2) => {
                  // Handle null/undefined values
                  if (!v1 && !v2) return 0;
                  if (!v1) return -1;
                  if (!v2) return 1;
                  // Convert to numbers and compare
                  return parseFloat(v1) - parseFloat(v2);
                },
                renderCell: (row) => renderCellSafely(row, key, 'string',
                  (r, value) => value.toString()
                )
              };
            }
            // Default string rendering
            return {
              ...baseColumn,
              renderCell: (row) => renderCellSafely(row, key, 'string',
                (r, value) => value.toString()
              )
            };
        }
      }));

  // Add custom field columns if they exist
  if (customFieldDefs.length > 0) {
    customFieldDefs.forEach(field => {
      columns.push({
        field: `custom_field_${field.id}`,
        headerName: field.name,
        type: 'string',
        sortable: true,
        filterOperators: [
          { value: 'contains', label: 'Contains' },
          { value: 'equals', label: 'Equals' },
          { value: 'startsWith', label: 'Starts with' },
          { value: 'endsWith', label: 'Ends with' },
          { value: 'isEmpty', label: 'Is empty' },
          { value: 'isNotEmpty', label: 'Is not empty' }
        ],
        renderCell: (row) => {
          const getValue = () => {
            if (!customFieldValuesMap[row.id]) return '';
            
            const fieldValues = customFieldValuesMap[row.id];
            const fieldValue = fieldValues?.[field.id]?.value;
            
            if (!fieldValue) return '';
            
            try {
              // If it's a JSON string, parse it
              const parsed = JSON.parse(fieldValue);
              return parsed.value || '';
            } catch (e) {
              // If it's not JSON, return as is
              return fieldValue;
            }
          };

          // Store the value in the row data for sorting
          row[`custom_field_${field.id}`] = getValue();
          
          // Return display value
          return row[`custom_field_${field.id}`] || '-';
        }
      });
    });
  }

    // Apply column order if specified
    if (columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((field, index) => [field, index]));
      columns.sort((a, b) => {
        const aIndex = orderMap.has(a.field) ? orderMap.get(a.field) : Number.MAX_SAFE_INTEGER;
        const bIndex = orderMap.has(b.field) ? orderMap.get(b.field) : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
    }

    // Add sold price column
    columns.push({
      field: 'sale_price',
      headerName: 'Sold Price',
      type: 'number',
      sortable: true,
      filterOperators: [
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not equals' },
        { value: '>', label: 'Greater than' },
        { value: '<', label: 'Less than' },
        { value: '>=', label: 'Greater than or equals' },
        { value: '<=', label: 'Less than or equals' },
        { value: 'isEmpty', label: 'Is empty' },
        { value: 'isNotEmpty', label: 'Is not empty' }
      ],
      renderCell: (row) => {
        if (!row.sale_price) return '-';
        const price = parseFloat(row.sale_price);
        const hasDecimals = price % 1 !== 0;
        return `$${price.toLocaleString(undefined, {
          minimumFractionDigits: hasDecimals ? 2 : 0,
          maximumFractionDigits: hasDecimals ? 2 : 0
        })}`;
      },
      sortComparator: (v1, v2) => {
        if (v1 === null && v2 === null) return 0;
        if (v1 === null) return 1;
        if (v2 === null) return -1;
        return v1 - v2;
      }
    });

    // Add sale date column
    columns.push({
      field: 'sale_date',
      headerName: 'Sale Date',
      type: 'date',
      sortable: true,
      filterOperators: [
        { value: 'equals', label: 'Equals' },
        { value: 'notEquals', label: 'Not equals' },
        { value: '>', label: 'After' },
        { value: '<', label: 'Before' },
        { value: 'isEmpty', label: 'Is empty' },
        { value: 'isNotEmpty', label: 'Is not empty' }
      ],
      renderCell: (row) => {
        if (!row.sale_date) return '-';
        return formatDate(row.sale_date, { user });
      },
      sortComparator: (v1, v2) => {
        if (v1 === null && v2 === null) return 0;
        if (v1 === null) return 1;
        if (v2 === null) return -1;
        return new Date(v1) - new Date(v2);
      }
    });

    return columns;
  }, [coins, showThumbnails, columnOrder, density, customFieldValuesMap, customFieldDefs]);

  return { columns, customFieldValuesMap };
};

export const useRows = (coins = [], customFieldValuesMap = {}) => {
  return useMemo(() => {
    try {
      if (!Array.isArray(coins)) return [];
      
      return coins.map(coin => {
        // Use custom_fields directly from the SQL response
        const customFields = coin.custom_fields || {};

        return {
          id: coin.id,
          ...coin,
          custom_fields: customFields,
          href: `/coins/${coin.id}`,
          imageUrl: coin.images?.length && coin.images[0]?.thumbnail_path 
            ? images.getImage(coin.images[0].thumbnail_path, coin.images[0].source)
            : '/images/placeholder.svg'
        };
      });
    } catch (error) {
      console.error('Error processing coins:', error);
      return [];
    }
  }, [coins]);
};
